/**
 * #1124 — Data pressure card: where the market-data flood reaching THIS tab
 * comes from, by feed, with how much of it reached the screen, how far the tab
 * trails the feed worker, and the per-connection pull. Built only from frames
 * the browser already received; nothing here talks to hydra or the relay.
 *
 * The sampled state lives in this component (useFeedPressure /
 * useFeedConnections), so a sample re-renders the card alone. Seats pass only
 * stable props (#836).
 */
import { memo, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/lib/infra/utils';
import { TAB_LAG_LIMIT_MS } from '@/clients/HydraClient';
import { readConnectionsNow, useFeedConnections, useFeedPressure, type FeedConnections, type FeedPressureSample, type VenueRate } from '@/hooks/useFeedPressure';
import { collectReportContext } from '@/lib/telemetry/collectReportContext';
import { formatCharsPerSec, type ReportGame } from '@/lib/telemetry/reportContext';
import {
  MEMORY_CAVEAT, VENUE_LABEL, buildPressureReport, formatAboutBytes, formatBlocked, formatClockLocal, formatIn5m,
  formatLag, formatLagShort, formatMarketViews, formatMemoryGrowth, formatPct, formatRate, formatTopCounts,
} from '@/lib/telemetry/pressureReport';

export interface DataPressureCardProps {
  /** `rail`: Game Detail's live rail. `prong`: a section of the Positions rail. */
  variant: 'rail' | 'prong';
  expanded: boolean;
  onToggleExpanded: () => void;
  /** Named in the Copy report ("Game Detail", "Positions"). */
  page: string;
  /** The game this page shows, for the Copy report. Absent where there is no single game. */
  game?: ReportGame | null;
}

const COPIED_MS = 2000;

function buildCommit(): string {
  return typeof __BUILD_COMMIT__ === 'string' && /^[a-f0-9]{7,40}$/.test(__BUILD_COMMIT__) ? __BUILD_COMMIT__ : 'unknown';
}

function venueTotal(v: VenueRate): number | null {
  if (v.marketsPerSec == null) return null;
  return (v.booksPerSec ?? 0) + v.marketsPerSec;
}

/** The collapsed line, most important first, because the w-96 rail leaves it
 *  about 215 px: is the tab behind, how much is arriving, and from where. The
 *  full per-feed line is its title. */
function summaryOf(sample: FeedPressureSample): string {
  let total: number | null = null;
  let busiest: VenueRate | null = null;
  for (const v of sample.venues) {
    const t = venueTotal(v);
    if (t == null) continue;
    total = (total ?? 0) + t;
    if (busiest == null || t > (venueTotal(busiest) ?? 0)) busiest = v;
  }
  const parts = [`Tab ${formatLagShort(sample.lagMs)} behind`, `${formatRate(total)}/s`];
  if (busiest != null && (venueTotal(busiest) ?? 0) > 0) parts.push(`most ${VENUE_LABEL[busiest.venue]}`);
  return parts.join(' · ');
}

function fullLineOf(sample: FeedPressureSample): string {
  const venues = sample.venues.map((v) => `${VENUE_LABEL[v.venue]} ${formatRate(venueTotal(v))}/s`).join(' · ');
  const delivered = sample.holdings ? `${sample.holdings.marketsDelivered.toLocaleString('en-US')} markets` : '—';
  return `${venues} · Tab behind ${formatLag(sample.lagMs)} · Delivered ${delivered} · Memory ${formatAboutBytes(sample.memory?.usedBytes ?? null)}`;
}

/** Main thread blocked over 30 % of the last 10 s turns amber (a judgment call). */
const LONG_TASK_AMBER_MS = 3_000;
const LINE = 'truncate text-[11px] leading-[1.0625rem] tabular-nums';

/** One fixed-height line of the expanded body: it truncates, never wraps, and
 *  renders "—" rather than disappearing, so a state change moves nothing. A
 *  `tail` ("· 11 more", a time) never shrinks: the label truncates before it. */
function ReadingLine({ testId, title, amber, tail, children }: {
  testId: string; title?: string; amber?: boolean; tail?: string; children: string;
}) {
  const color = amber ? 'text-amber-400' : 'text-muted-foreground';
  if (!tail) {
    return <p data-testid={testId} title={title} className={cn(LINE, 'h-[1.0625rem]', color)}>{children}</p>;
  }
  return (
    <p data-testid={testId} title={title} className={cn('flex h-[1.0625rem] text-[11px] leading-[1.0625rem] tabular-nums', color)}>
      <span className="min-w-0 truncate">{children}</span>
      <span data-testid={`${testId}-more`} className="shrink-0 whitespace-pre">{tail}</span>
    </p>
  );
}

/** " · N more" after the first of `count` items; none when there is one. */
function moreTail(count: number): string | undefined {
  return count > 1 ? ` · ${count - 1} more` : undefined;
}

function MarketsOpen({ sample }: { sample: FeedPressureSample }) {
  const h = sample.holdings;
  const n = (x: number) => x.toLocaleString('en-US');
  if (h == null) {
    return (
      <>
        <ReadingLine testId="data-pressure-market-views">Views asking for markets —</ReadingLine>
        <ReadingLine testId="data-pressure-book-views">Books needed —</ReadingLine>
        <ReadingLine testId="data-pressure-delivered">Delivered —</ReadingLine>
        <ReadingLine testId="data-pressure-windows">g1 windows —</ReadingLine>
      </>
    );
  }
  const sports = h.marketsBySport.map((b) => `${b.sport.toUpperCase()} ${n(b.markets)}`).join(' · ');
  return (
    <>
      <ReadingLine
        testId="data-pressure-market-views"
        title={`Views in this tab widening what the markets feed carries: ${formatMarketViews(h.marketViews, h.marketViews.length) || 'none'}`}
        tail={moreTail(h.marketViews.length)}
      >
        {`Views asking for markets ${h.marketViews.length}${h.marketViews.length > 0 ? `: ${formatMarketViews(h.marketViews.slice(0, 1), 1, { sports: 1, games: false, moreSports: false })}` : ''}`}
      </ReadingLine>
      <ReadingLine
        testId="data-pressure-book-views"
        title={`Views sharing a market cost one book, so the total can be less than the sum. Routed per connection: ${formatTopCounts(h.booksByConnection, h.booksByConnection.length) || 'none'}`}
        tail={moreTail(h.bookViews.length)}
      >
        {`Books needed ${n(h.booksNeeded)}${h.bookViews.length > 0 ? `: ${formatTopCounts(h.bookViews.slice(0, 1), 1)}` : ''}`}
      </ReadingLine>
      <ReadingLine
        testId="data-pressure-delivered"
        title={`What hydra sent this tab under its filter.${sports ? ` ${sports} (a market can count under two sports).` : ''}`}
      >
        {`Delivered ${n(h.marketsDelivered)} markets · ${n(h.booksDelivered)} books`}
      </ReadingLine>
      <ReadingLine
        testId="data-pressure-windows"
        title={h.windows == null
          ? 'Not reported yet, or no shared feed worker in this browser'
          : 'g1 windows attached to this browser’s feed worker. A closed one can count for up to 10 min; the fills popup counts as a window.'}
      >
        {h.windows == null ? 'g1 windows —' : `g1 windows ${h.windows} sharing a feed`}
      </ReadingLine>
    </>
  );
}

function memoryTitle(sample: FeedPressureSample): string {
  const m = sample.memory;
  if (m == null) return "This browser doesn't report page memory";
  const limit = m.limitBytes == null ? '' : ` Limit ${formatAboutBytes(m.limitBytes)}.`;
  return `Page script memory. ${MEMORY_CAVEAT}, so it can be up to 20 min old.${limit}`;
}

function longTaskLine(sample: FeedPressureSample): { text: string; title: string; amber: boolean } {
  if (sample.hidden) return { text: 'Main thread —', title: 'Paused while tab hidden', amber: false };
  const t = sample.longTasks;
  if (t == null) return { text: 'Main thread —', title: "This browser doesn't report long tasks", amber: false };
  return {
    text: `Main thread blocked ${formatBlocked(t.blockedMs)} of the last 10 s`,
    title: `Time the page could not respond: ${t.count.toLocaleString('en-US')} tasks over 50 ms in the last 10 s. Shorter tasks aren't counted.`,
    amber: t.blockedMs > LONG_TASK_AMBER_MS,
  };
}

function churnLines(sample: FeedPressureSample): { resets: string; resnapshots: string; title?: string } {
  const c = sample.churn;
  if (c == null) return { resets: 'Connection resets —', resnapshots: 'Resnapshots requested —' };
  const n = (x: number) => x.toLocaleString('en-US');
  const worst = c.topResets.map((r) => `${r.label} ${n(r.resets)}`).join(' · ');
  return {
    resets: `Connection resets ${n(c.resets)} (${formatIn5m(c.resetsIn5m, c.resetsIn5mCapped)})`,
    resnapshots: `Resnapshots requested ${n(c.resnapshots)} (${formatIn5m(c.resnapshotsIn5m, c.resnapshotsIn5mCapped)})`,
    title: `This tab's counts since its feeds connected. ${n(c.upstream)} of the resets ${c.upstream === 1 ? 'was' : 'were'} the upstream socket itself.${worst ? ` Most: ${worst}.` : ''}`,
  };
}

function coalescedText(sample: FeedPressureSample): { label: string; since: string } | null {
  const c = sample.coalesced;
  if (c == null || c.connections === 0) return null;
  return {
    // Observational (#1124, Nadya 10/12): "reported", never "since", which reads as still coalescing.
    // The legacy level's wording is longer, and the hover names books, so the line stays whole.
    label: `${c.paused ? 'Paused or coalesced' : 'Coalesced'} on ${c.connections}/${c.total} sockets`,
    since: c.firstSinceMs == null ? '' : ` \u00b7 reported ${formatClockLocal(c.firstSinceMs)}`,
  };
}

const COALESCED_TITLE = 'Hydra slowed (coalesced) or, at its older level, paused book updates on these sockets. Hydra says this once per socket and sends no all-clear, so the report is kept until that socket closes (a pause also ends when books resume); it says hydra coalesced from that time, not that it still is. Windows sharing this browser\u2019s feed worker show it too, including one opened later; a window on its own direct socket shows only what that socket reported. The time is when it was first heard on that socket.';

const COLUMN_TITLES = {
  rates: "Frames this tab received in the last second, including ones it then didn't need",
  shown: "Share of received books that reached something on screen. Poly US books go to the US board whether or not it's shown, so they aren't measured here",
  peak: 'Highest books + markets in one second over the last minute',
} as const;

/** The live card: samplers + view. Seats mount this. Memoized: Game Detail
 *  re-renders on every feed flush, and its props are stable, so a seat render
 *  costs the card nothing. */
export const DataPressureCard = memo(function DataPressureCard(props: DataPressureCardProps) {
  const sample = useFeedPressure();
  const [connectionsOpen, setConnectionsOpen] = useState(false);
  const connections = useFeedConnections(connectionsOpen);
  const toggleConnections = useCallback(() => setConnectionsOpen((open) => !open), []);
  return (
    <DataPressureCardView
      {...props}
      sample={sample}
      connections={connections}
      connectionsOpen={connectionsOpen}
      onToggleConnections={toggleConnections}
    />
  );
});

export interface DataPressureCardViewProps extends DataPressureCardProps {
  sample: FeedPressureSample;
  connections: FeedConnections | null;
  connectionsOpen: boolean;
  onToggleConnections: () => void;
}

/** Presentational card over a sample. The Layout Lab renders this with fixtures. */
export function DataPressureCardView({
  variant, expanded, onToggleExpanded, page, game = null, sample, connections, connectionsOpen, onToggleConnections,
}: DataPressureCardViewProps) {
  const [copyState, setCopyState] = useState<{ status: 'idle' | 'copied' | 'failed'; text: string }>({ status: 'idle', text: '' });
  const bodyId = useId();

  // Latest values for the stable Copy handler, written in the commit phase.
  const latest = useRef<{ sample: FeedPressureSample; connections: FeedConnections | null; connectionsOpen: boolean; page: string; game: ReportGame | null }>({
    sample, connections, connectionsOpen, page, game,
  });
  useLayoutEffect(() => {
    latest.current = { sample, connections, connectionsOpen, page, game };
  });

  const copyReport = useCallback(() => {
    const { sample: s, connections: listed, connectionsOpen: isOpen, page: p, game: g } = latest.current;
    const view = { page: p, game: g };
    // The open list's sample, or 'opening' before its first one lands; with
    // the list closed, one read now (a direct socket's window rolls on read,
    // so never read beside the list).
    const connections = isOpen ? (listed ?? 'opening') : readConnectionsNow();
    const text = buildPressureReport({ sample: s, connections, view, context: collectReportContext(view), build: buildCommit() });
    const write = typeof navigator !== 'undefined' && navigator.clipboard?.writeText
      ? navigator.clipboard.writeText(text)
      : Promise.reject(new Error('no clipboard'));
    write.then(
      () => setCopyState({ status: 'copied', text: '' }),
      () => setCopyState({ status: 'failed', text }),
    );
  }, []);

  // "Copied" reverts after a moment; the timer goes with the card.
  useEffect(() => {
    if (copyState.status !== 'copied') return;
    const t = setTimeout(() => setCopyState({ status: 'idle', text: '' }), COPIED_MS);
    return () => clearTimeout(t);
  }, [copyState.status]);

  const lagging = sample.lagMs != null && sample.lagMs > TAB_LAG_LIMIT_MS;
  const summary = sample.hidden ? 'Paused while tab hidden' : summaryOf(sample);
  const summaryTitle = sample.hidden ? 'Paused while tab hidden' : fullLineOf(sample);
  const longTask = longTaskLine(sample);
  const churn = churnLines(sample);
  const coalesced = coalescedText(sample);

  return (
    <section
      aria-label="Data pressure"
      data-testid="data-pressure-card"
      data-variant={variant}
      // The Positions rail docks its prongs flush: square corners, no side borders.
      className={cn('bg-card border-border flex shrink-0 flex-col overflow-hidden border', variant === 'prong' ? 'rounded-none border-x-0' : 'rounded-lg')}
    >
      <div className="border-border flex shrink-0 items-center gap-2 border-b px-4 py-1">
        <h2 className="shrink-0 text-[10px] font-semibold">Data pressure</h2>
        <span
          data-testid="data-pressure-summary"
          className={cn('min-w-0 flex-1 truncate text-[10px] tabular-nums', lagging ? 'text-amber-400' : 'text-muted-foreground')}
          title={summaryTitle}
        >
          {summary}
        </span>
        <button
          type="button"
          className="text-primary shrink-0 rounded px-1 py-0.5 text-[11px] hover:bg-muted"
          aria-expanded={expanded}
          aria-controls={bodyId}
          onClick={onToggleExpanded}
        >
          {expanded ? 'Less' : 'More'}
        </button>
      </div>
      {expanded && (
        <div id={bodyId} className="flex min-h-0 flex-col">
        {/* Only the readings scroll; the footer below is pinned, so nothing is
            ever cut through at the bottom of the scroll area. */}
        <div data-testid="data-pressure-body" className="max-h-[min(32vh,300px)] min-h-0 overflow-y-auto px-4 py-2 [scrollbar-gutter:stable]">
          {/* Josh's two causes first: what this tab has open, and its memory. */}
          <MarketsOpen sample={sample} />
          {/* Two fixed lines, so the approved wording fits the rail without an ellipsis. */}
          <ReadingLine testId="data-pressure-memory" title={memoryTitle(sample)}>
            {`Memory ${formatAboutBytes(sample.memory?.usedBytes ?? null)}`}
          </ReadingLine>
          <ReadingLine testId="data-pressure-memory-growth" title={memoryTitle(sample)}>
            {sample.memory == null ? '' : formatMemoryGrowth(sample.memory)}
          </ReadingLine>
          <table className="mt-1 w-full table-fixed text-[11px] tabular-nums">
            <thead className="text-muted-foreground text-[10px]">
              <tr>
                <th className="w-[26%] py-0.5 text-left font-medium">Feed</th>
                <th className="py-0.5 text-right font-medium" title={COLUMN_TITLES.rates}>Books/s</th>
                <th className="py-0.5 text-right font-medium" title={COLUMN_TITLES.rates}>Markets/s</th>
                <th className="py-0.5 text-right font-medium" title={COLUMN_TITLES.shown}>Books shown</th>
                <th className="py-0.5 text-right font-medium" title={COLUMN_TITLES.peak}>Peak 60s</th>
              </tr>
            </thead>
            <tbody>
              {sample.venues.map((v) => (
                <tr
                  key={v.venue}
                  data-testid={`data-pressure-venue-${v.venue}`}
                  title={v.venue === 'odds' ? `Unabated ${formatRate(v.unabatedPerSec ?? null)}/s · OddsPapi ${formatRate(v.oddspapiPerSec ?? null)}/s` : undefined}
                >
                  <td className="py-0.5">{VENUE_LABEL[v.venue]}</td>
                  {/* While hidden each "last second" is a 6 s average, so it isn't shown. */}
                  <td className="py-0.5 text-right">{formatRate(sample.hidden ? null : v.booksPerSec)}</td>
                  <td className="py-0.5 text-right">{formatRate(sample.hidden ? null : v.marketsPerSec)}</td>
                  <td className="py-0.5 text-right">{formatPct(sample.hidden ? null : v.shownPct)}</td>
                  <td className="py-0.5 text-right">{formatRate(v.peakPerSec)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <ReadingLine testId="data-pressure-longtasks" title={longTask.title} amber={longTask.amber}>{longTask.text}</ReadingLine>
          <p data-testid="data-pressure-lag" className={cn('text-[11px] tabular-nums', lagging ? 'text-amber-400' : 'text-muted-foreground')}>
            Tab behind {formatLag(sample.lagMs)} (5 s sample)
          </p>
          {/* The line's space is reserved, so hydra reporting backpressure moves nothing below it. */}
          <p
            data-testid="data-pressure-coalesced"
            title={coalesced ? COALESCED_TITLE : undefined}
            className="flex min-h-[1.0625rem] text-[11px] leading-[1.0625rem] text-amber-400"
          >
            {coalesced && (
              <>
                <span className="min-w-0 truncate">{coalesced.label}</span>
                {/* The time is the point of the line, so the label truncates before it. */}
                <span data-testid="data-pressure-coalesced-since" className="shrink-0 whitespace-pre">{coalesced.since}</span>
              </>
            )}
          </p>
          <ReadingLine testId="data-pressure-resets" title={churn.title}>{churn.resets}</ReadingLine>
          <ReadingLine testId="data-pressure-resnapshots" title={`${churn.title ?? ''} Copy report includes the last 32 snapshot/subscription sends per connection, with caller reasons and worker ports when available.`}>{churn.resnapshots}</ReadingLine>
          <div className="mt-2">
            <button
              type="button"
              className="text-primary rounded px-1 py-0.5 text-[11px] hover:bg-muted"
              aria-expanded={connectionsOpen}
              onClick={onToggleConnections}
            >
              {connections ? `Connections (${connections.total})` : 'Connections'}
            </button>
            {connectionsOpen && (
              <div data-testid="data-pressure-connections" className="mt-1">
                {/* The breakdown sits above the list, so the header's count is
                    explained before the list scrolls inside the card's cap. */}
                <p data-testid="data-pressure-connections-summary" className="text-muted-foreground text-[10px]">
                  {connections == null
                    ? "5 s window · this browser's sockets"
                    : `${connections.total} sockets: ${connections.rows.length} busy · ${connections.idle} idle · ${connections.starting > 0 ? `${connections.starting} starting · ` : ''}${connections.reconnecting} reconnecting · 5 s window`}
                </p>
                {connections == null ? (
                  <p className="text-muted-foreground text-[11px]">—</p>
                ) : (
                  <table className="w-full table-fixed text-[11px] tabular-nums">
                    <thead className="text-muted-foreground text-[10px]">
                      <tr>
                        <th className="w-[34%] py-0.5 text-left font-medium">Engine</th>
                        <th className="py-0.5 text-right font-medium">Frames/s</th>
                        <th className="py-0.5 text-right font-medium" title="JSON characters per second this machine received and decoded, not network bytes">Chars/s</th>
                        <th className="py-0.5 text-right font-medium">Tabs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {connections.rows.map((r) => (
                        <tr key={r.id}>
                          <td
                            className="truncate py-0.5"
                            title={r.backpressureLevel >= 1
                              ? `${r.label}: books ${r.backpressureLevel >= 2 ? 'paused' : 'coalesced'} by hydra${r.backpressureSinceMs == null ? '' : `, first heard at ${formatClockLocal(r.backpressureSinceMs)}`}`
                              : r.label}
                          >
                            {r.label}
                            {r.backpressureLevel >= 1 && (
                              <span className="text-amber-400">{r.backpressureLevel >= 2 ? ' · paused' : ' · coalesced'}</span>
                            )}
                          </td>
                          <td className="py-0.5 text-right">{formatRate(r.framesPerSec)}</td>
                          <td className="py-0.5 text-right">{formatCharsPerSec(r.bytesPerSec)}</td>
                          <td className="py-0.5 text-right">{r.tabs}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </div>
          <div data-testid="data-pressure-footer" className="border-border flex shrink-0 items-center justify-between gap-2 border-t px-4 py-1.5">
            <p className="text-muted-foreground text-[10px]">
              This tab, last second. Frames dropped before reaching it count only under Connections.
            </p>
            <button
              type="button"
              className="text-primary shrink-0 rounded px-1 py-0.5 text-[11px] hover:bg-muted"
              onClick={copyReport}
            >
              {copyState.status === 'copied' ? 'Copied' : copyState.status === 'failed' ? "Couldn't copy" : 'Copy report'}
            </button>
          </div>
          {copyState.status === 'failed' && (
            <div className="shrink-0 px-4 pb-2">
              <textarea
                readOnly
                aria-label="Data pressure report"
                className="bg-muted h-24 w-full rounded p-1 font-mono text-[10px]"
                value={copyState.text}
                onFocus={(e) => e.currentTarget.select()}
                autoFocus
              />
            </div>
          )}
        </div>
      )}
    </section>
  );
}
